import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import RichTextEditor from '../RichTextEditor/RichTextEditor';
import TrackedChangesView from '../TrackedChangesView/TrackedChangesView';
import '../../styles/ampDescriptionDisplay.css';

const API_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' && window.location.port === '3002'
    ? 'http://localhost:3000/api'
    : '/api');

// Section type configuration for labels, icons, and placeholders
const SECTION_CONFIG = {
  description: { label: 'Description', icon: '📝', placeholder: 'Edit the amp description...' },
  maintenance: { label: 'Maintenance & Tips', icon: '🔧', placeholder: 'Share maintenance tips, bias procedures, common issues, mods, recap guides...' },
  variants: { label: 'Variants & Revisions', icon: '🔀', placeholder: 'Document circuit changes by serial range, limited editions, regional differences...' },
  notable_users: { label: 'Notable Users', icon: '🎸', placeholder: 'List famous players associated with this amp, with context on how they used it...' },
  amp_builders: { label: 'Amp Builders', icon: '🛠️', placeholder: 'Share links to kits, parts suppliers, schematics, wiring diagrams, and build resources...' },
};

/**
 * WikiSuggestEdit — "Suggest Edit" button + proposal modal for amp wiki sections.
 * 
 * Props:
 *   amplifierId — the amp ID
 *   sectionType — wiki section type (default: 'description')
 *   currentContent — the current section content HTML
 *   rawContent — the raw content string (may be __RAW_HTML__ prefixed or Draft.js JSON)
 *   isOwner — whether the current user owns this amp
 *   onEditApplied — callback when an auto-approved edit is applied (to refresh amp data)
 *   showPendingReview — whether to show the pending review badge/panel (default: true)
 *   showHistory — whether to show the history toggle (default: true)
 *   compact — if true, uses a smaller button style (for additional sections)
 */
const WikiSuggestEdit = ({ amplifierId, currentDescription, rawDescription, currentContent, rawContent, sectionType = 'description', isOwner, onEditApplied, autoResubmitRevisionId, onResubmitHandled, showPendingReview = true, showHistoryToggle = true, compact = false }) => {
  // Support both old props (currentDescription/rawDescription) and new props (currentContent/rawContent)
  const effectiveContent = currentContent !== undefined ? currentContent : currentDescription;
  const effectiveRawContent = rawContent !== undefined ? rawContent : rawDescription;
  const sectionConfig = SECTION_CONFIG[sectionType] || SECTION_CONFIG.description;
  const { token, isAuthenticated, user } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [editorContent, setEditorContent] = useState('');
  const [editSummary, setEditSummary] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null); // { success, message }
  const [summaryNeeded, setSummaryNeeded] = useState(false);
  const summaryInputRef = React.useRef(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Pending reviews panel
  const [showPending, setShowPending] = useState(false);
  const [pendingRevisions, setPendingRevisions] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [reviewNote, setReviewNote] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [selectedRevision, setSelectedRevision] = useState(null);
  const [reviewEditorContent, setReviewEditorContent] = useState(''); // moderator's edited content
  const [reviewContentModified, setReviewContentModified] = useState(false); // track if moderator edited

  // Changes requested for the current user
  const [changesRequested, setChangesRequested] = useState([]); // revisions needing user's attention
  const [resubmitRevisionId, setResubmitRevisionId] = useState(null); // when re-editing
  const [resubmitReviewNote, setResubmitReviewNote] = useState(''); // reviewer's note for changes_requested
  const [resubmitHasSuggestedEdits, setResubmitHasSuggestedEdits] = useState(false); // reviewer made edits
  const [resubmitOriginalContent, setResubmitOriginalContent] = useState(''); // user's original content for tracked changes
  const [resubmitSuggestedContent, setResubmitSuggestedContent] = useState(''); // reviewer's version

  // Fetch pending count for owners/admins
  const fetchPendingCount = useCallback(async () => {
    if (!isAuthenticated || !amplifierId) return;
    try {
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const resp = await fetch(`${API_URL}/wiki/${amplifierId}/sections`, { headers });
      if (resp.ok) {
        const data = await resp.json();
        setPendingCount(data.pending_count || 0);
      }
    } catch (e) { /* silent */ }
  }, [isAuthenticated, amplifierId, token]);

  useEffect(() => {
    fetchPendingCount();
  }, [fetchPendingCount]);

  // Fetch changes_requested revisions for this user on this amp
  const fetchChangesRequested = useCallback(async () => {
    if (!isAuthenticated || !amplifierId || !token) return;
    try {
      const resp = await fetch(`${API_URL}/wiki/my-pending-changes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        // Filter to this amp and section type
        setChangesRequested((data.revisions || []).filter(r => 
          r.amplifier_id === parseInt(amplifierId) && r.section_type === sectionType
        ));
      }
    } catch (e) { /* silent */ }
  }, [isAuthenticated, amplifierId, token, sectionType]);

  useEffect(() => {
    fetchChangesRequested();
  }, [fetchChangesRequested]);

  // Auto-open resubmit modal when deep-linked from a notification
  useEffect(() => {
    if (!autoResubmitRevisionId || !token) return;
    const autoOpen = async () => {
      try {
        const resp = await fetch(`${API_URL}/wiki/revisions/${autoResubmitRevisionId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (resp.ok) {
          const data = await resp.json();
          // API returns the revision as the top-level object
          const rev = data.revision || data;
          if (rev && rev.status === 'changes_requested') {
            openModalForResubmit(rev);
          }
        }
      } catch (e) {
        console.error('Failed to auto-open resubmit:', e);
      }
      // Clear the query param via React Router so the state transition
      // is tracked — allows re-clicking the notification to work
      if (onResubmitHandled) onResubmitHandled();
    };
    autoOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoResubmitRevisionId, token]);

  const openModal = () => {
    // Parse the raw content to get editable HTML
    let html = '';
    if (effectiveRawContent) {
      if (effectiveRawContent.startsWith('__RAW_HTML__')) {
        html = effectiveRawContent.replace('__RAW_HTML__', '');
      } else {
        try {
          // Draft.js JSON — use the already-parsed HTML from currentContent
          html = effectiveContent || '';
        } catch {
          html = effectiveRawContent || '';
        }
      }
    }
    setEditorContent(html);
    setEditSummary('');
    setSubmitResult(null);
    setSummaryNeeded(false);
    setResubmitRevisionId(null);
    setShowModal(true);
  };

  // Open modal pre-filled with a changes_requested revision's content
  // If the reviewer provided suggested edits, use those instead of the user's original
  const openModalForResubmit = (rev) => {
    const source = rev.reviewer_suggested_content || rev.content;
    let html = '';
    if (source) {
      html = source.startsWith('__RAW_HTML__')
        ? source.replace('__RAW_HTML__', '')
        : source;
    }
    setEditorContent(html);
    setEditSummary(rev.edit_summary || '');
    setSubmitResult(null);
    setSummaryNeeded(false);
    setResubmitRevisionId(rev.revision_id);
    setResubmitReviewNote(rev.review_note || '');
    setResubmitHasSuggestedEdits(!!rev.reviewer_suggested_content);
    setResubmitOriginalContent(rev.content || '');
    setResubmitSuggestedContent(rev.reviewer_suggested_content || '');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditorContent('');
    setEditSummary('');
    setSubmitResult(null);
    setSummaryNeeded(false);
    setResubmitRevisionId(null);
    setResubmitReviewNote('');
    setResubmitHasSuggestedEdits(false);
    setResubmitOriginalContent('');
    setResubmitSuggestedContent('');
  };

  const handleSubmit = async () => {
    if (sectionType === 'description' && !editorContent.trim()) return;
    if (!editSummary.trim()) {
      setSummaryNeeded(true);
      if (summaryInputRef.current) {
        summaryInputRef.current.focus();
        summaryInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    setSummaryNeeded(false);
    setSubmitting(true);
    setSubmitResult(null);

    try {
      // If resubmitting a changes_requested revision, use the resubmit endpoint
      const url = resubmitRevisionId
        ? `${API_URL}/wiki/revisions/${resubmitRevisionId}/resubmit`
        : `${API_URL}/wiki/${amplifierId}/propose`;

      const body = resubmitRevisionId
        ? { content: `__RAW_HTML__${editorContent}`, edit_summary: editSummary }
        : { section_type: sectionType, content: `__RAW_HTML__${editorContent}`, edit_summary: editSummary };

      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      const data = await resp.json();

      if (resp.ok) {
        setSubmitResult({ success: true, message: data.message });
        if (data.status === 'approved' && onEditApplied) {
          onEditApplied();
        }
        // Auto-close after 2s on success
        setTimeout(() => closeModal(), 2000);
        fetchPendingCount();
        fetchChangesRequested();
      } else {
        setSubmitResult({ success: false, message: data.error || 'Failed to submit edit' });
      }
    } catch (error) {
      setSubmitResult({ success: false, message: 'Network error — please try again' });
    }
    setSubmitting(false);
  };

  // Fetch edit history
  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const resp = await fetch(`${API_URL}/wiki/${amplifierId}/sections/${sectionType}/history`);
      if (resp.ok) {
        const data = await resp.json();
        setHistory(data.revisions || []);
      }
    } catch (e) { /* silent */ }
    setHistoryLoading(false);
  };

  const toggleHistory = () => {
    if (!showHistory) {
      fetchHistory();
    }
    setShowHistory(!showHistory);
    setShowPending(false);
  };

  // Fetch pending revisions for review
  const fetchPending = async () => {
    setPendingLoading(true);
    try {
      const resp = await fetch(`${API_URL}/wiki/pending`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        // Filter to this amp only
        setPendingRevisions((data.revisions || []).filter(r => r.amplifier_id === parseInt(amplifierId)));
      }
    } catch (e) { /* silent */ }
    setPendingLoading(false);
  };

  const togglePending = () => {
    if (!showPending) {
      fetchPending();
    }
    setShowPending(!showPending);
    setShowHistory(false);
  };

  // Review a revision
  const handleReview = async (revisionId, action) => {
    // Require a note when requesting changes
    if (action === 'request_changes' && !reviewNote.trim()) {
      alert('Please describe what changes you would like the contributor to make.');
      return;
    }
    setReviewSubmitting(true);
    try {
      // Include was_modified flag so API can track clean approvals for trust system
      const body = { action, review_note: reviewNote || undefined, was_modified: reviewContentModified };
      if (action === 'request_changes' && reviewContentModified && reviewEditorContent.trim()) {
        body.suggested_content = `__RAW_HTML__${reviewEditorContent}`;
      }

      const resp = await fetch(`${API_URL}/wiki/revisions/${revisionId}/review`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      if (resp.ok) {
        // Refresh pending list
        fetchPending();
        fetchPendingCount();
        setSelectedRevision(null);
        setReviewNote('');
        setReviewEditorContent('');
        setReviewContentModified(false);
        if (action === 'approve' && onEditApplied) {
          onEditApplied();
        }
      }
    } catch (e) { /* silent */ }
    setReviewSubmitting(false);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const canReview = isOwner || user?.is_admin;

  return (
    <>
      {/* Button row — Suggest Edit + Pending badge + History */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
        {isAuthenticated && (
          <button
            onClick={openModal}
            style={{
              background: 'transparent',
              color: '#2563eb',
              border: '1px solid #2563eb',
              borderRadius: '8px',
              padding: '0.4rem 0.8rem',
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '0.3rem',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#2563eb';
              e.currentTarget.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = '#2563eb';
            }}
            title={`Suggest an edit to ${sectionConfig.label.toLowerCase()}`}
          >
            {compact && !effectiveContent
              ? `${sectionConfig.icon} ${sectionConfig.label}`
              : `✏️ Suggest Edit${compact ? '' : ''}`
            }
          </button>
        )}

        {showPendingReview && canReview && pendingCount > 0 && (
          <button
            onClick={togglePending}
            style={{
              background: showPending ? '#f59e0b' : '#fef3c7',
              color: showPending ? 'white' : '#92400e',
              border: '1px solid #f59e0b',
              borderRadius: '8px',
              padding: '0.4rem 0.8rem',
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '0.3rem',
              transition: 'all 0.2s ease'
            }}
          >
            📋 {pendingCount} Pending {pendingCount === 1 ? 'Edit' : 'Edits'}
          </button>
        )}

        {showHistoryToggle && <button
          onClick={toggleHistory}
          style={{
            background: 'transparent',
            color: '#6b7280',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            padding: '0.4rem 0.8rem',
            cursor: 'pointer',
            fontSize: '0.85rem',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '0.3rem',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#9ca3af';
            e.currentTarget.style.color = '#374151';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#d1d5db';
            e.currentTarget.style.color = '#6b7280';
          }}
        >
          📜 Edit History
        </button>}
      </div>

      {/* Changes Requested Banner — shown to the contributor */}
      {changesRequested.length > 0 && (
        <div style={{
          marginTop: '0.75rem',
          background: '#fef3c7',
          borderRadius: '10px',
          border: '1px solid #fcd34d',
          padding: '0.75rem 1rem'
        }}>
          {changesRequested.map(rev => (
            <div key={rev.revision_id} style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <span style={{ fontSize: '1.1rem' }}>🔄</span>
                <strong style={{ color: '#92400e', fontSize: '0.9rem' }}>Changes Requested</strong>
                {rev.reviewer_display_name && (
                  <span style={{ color: '#b45309', fontSize: '0.85rem' }}>
                    by {rev.reviewer_display_name}
                  </span>
                )}
              </div>
              {rev.review_note && (
                <div style={{
                  background: 'white',
                  border: '1px solid #fde68a',
                  borderRadius: '8px',
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.9rem',
                  color: '#78350f',
                  lineHeight: '1.4'
                }}>
                  "{rev.review_note}"
                </div>
              )}
              {rev.reviewer_suggested_content && (
                <div style={{
                  fontSize: '0.85rem',
                  color: '#065f46',
                  fontWeight: '600'
                }}>
                  ✏️ The reviewer also made suggested edits to your text.
                </div>
              )}
              <button
                onClick={() => openModalForResubmit(rev)}
                style={{
                  alignSelf: 'flex-start',
                  background: '#f59e0b',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '0.4rem 0.8rem',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.3rem'
                }}
              >
                ✏️ Revise & Resubmit
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Edit History Panel */}
      {showHistory && (
        <div style={{
          marginTop: '1rem',
          background: '#f9fafb',
          borderRadius: '10px',
          border: '1px solid #e5e7eb',
          padding: '1rem',
          maxHeight: '300px',
          overflowY: 'auto'
        }}>
          <h4 style={{ margin: '0 0 0.75rem 0', color: '#374151', fontSize: '1rem' }}>
            Edit History
          </h4>
          {historyLoading ? (
            <p style={{ color: '#9ca3af', fontStyle: 'italic' }}>Loading...</p>
          ) : history.length === 0 ? (
            <p style={{ color: '#9ca3af', fontStyle: 'italic' }}>No edit history yet</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {history.map(rev => (
                <div key={rev.revision_id} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.5rem 0.75rem',
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '0.85rem'
                }}>
                  <span style={{
                    padding: '0.15rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    background: rev.status === 'approved' ? '#d1fae5' : rev.status === 'rejected' ? '#fee2e2' : rev.status === 'changes_requested' ? '#e0e7ff' : '#fef3c7',
                    color: rev.status === 'approved' ? '#065f46' : rev.status === 'rejected' ? '#991b1b' : rev.status === 'changes_requested' ? '#3730a3' : '#92400e'
                  }}>
                    {rev.status === 'changes_requested' ? 'changes requested' : rev.status}
                  </span>
                  <span style={{ color: '#374151', flex: 1 }}>
                    <strong>{rev.author_display_name}</strong>: {rev.edit_summary}
                  </span>
                  <span style={{ color: '#9ca3af', whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                    {formatDate(rev.created_at)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Pending Reviews Panel */}
      {showPending && canReview && (
        <div style={{
          marginTop: '1rem',
          background: '#fffbeb',
          borderRadius: '10px',
          border: '1px solid #fde68a',
          padding: '1rem'
        }}>
          <h4 style={{ margin: '0 0 0.75rem 0', color: '#92400e', fontSize: '1rem' }}>
            Pending Edits for Review
          </h4>
          {pendingLoading ? (
            <p style={{ color: '#b45309', fontStyle: 'italic' }}>Loading...</p>
          ) : pendingRevisions.length === 0 ? (
            <p style={{ color: '#b45309', fontStyle: 'italic' }}>No pending edits</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {pendingRevisions.map(rev => (
                <div key={rev.revision_id} style={{
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '10px',
                  padding: '1rem',
                  fontSize: '0.9rem'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span>
                      <strong>{rev.author_display_name}</strong> —{' '}
                      <em style={{ color: '#6b7280' }}>{rev.edit_summary}</em>
                    </span>
                    <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>{formatDate(rev.created_at)}</span>
                  </div>

                  {selectedRevision === rev.revision_id ? (
                    <>
                      {/* Editable Track Changes view — edit directly in the formatted diff */}
                      <TrackedChangesView
                        oldContent={rev.previous_content || ''}
                        newContent={rev.content || ''}
                        editable
                        onChange={(cleanHtml) => {
                          setReviewEditorContent(cleanHtml);
                          setReviewContentModified(true);
                        }}
                        style={{ marginBottom: '0.5rem' }}
                      />
                      {reviewContentModified && (
                        <div style={{ fontSize: '0.75rem', color: '#059669', marginBottom: '0.5rem', fontWeight: '600' }}>
                          ✓ You've made edits — click "Request Changes" to send your suggested version to the contributor.
                        </div>
                      )}

                      {/* Review note */}
                      <input
                        type="text"
                        placeholder="Review note (optional)"
                        value={reviewNote}
                        onChange={(e) => setReviewNote(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.5rem 0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '0.85rem',
                          marginBottom: '0.75rem',
                          boxSizing: 'border-box'
                        }}
                      />

                      {/* Review actions */}
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={() => handleReview(rev.revision_id, 'approve')}
                          disabled={reviewSubmitting}
                          style={{
                            background: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '0.5rem 1rem',
                            cursor: reviewSubmitting ? 'not-allowed' : 'pointer',
                            fontWeight: '600',
                            fontSize: '0.85rem'
                          }}
                        >
                          ✅ Approve
                        </button>
                        <button
                          onClick={() => handleReview(rev.revision_id, 'reject')}
                          disabled={reviewSubmitting}
                          style={{
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '0.5rem 1rem',
                            cursor: reviewSubmitting ? 'not-allowed' : 'pointer',
                            fontWeight: '600',
                            fontSize: '0.85rem'
                          }}
                        >
                          ❌ Reject
                        </button>
                        <button
                          onClick={() => handleReview(rev.revision_id, 'request_changes')}
                          disabled={reviewSubmitting}
                          style={{
                            background: '#f59e0b',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '0.5rem 1rem',
                            cursor: reviewSubmitting ? 'not-allowed' : 'pointer',
                            fontWeight: '600',
                            fontSize: '0.85rem'
                          }}
                        >
                          🔄 Request Changes
                        </button>
                        <button
                          onClick={() => { setSelectedRevision(null); setReviewNote(''); }}
                          style={{
                            background: '#f3f4f6',
                            color: '#374151',
                            border: '1px solid #d1d5db',
                            borderRadius: '8px',
                            padding: '0.5rem 1rem',
                            cursor: 'pointer',
                            fontWeight: '500',
                            fontSize: '0.85rem'
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  ) : (
                    <button
                      onClick={() => { setSelectedRevision(rev.revision_id); setReviewNote(''); setReviewContentModified(false); }}
                      style={{
                        background: '#f3f4f6',
                        color: '#374151',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        padding: '0.4rem 0.8rem',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: '500'
                      }}
                    >
                      Review this edit
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Suggest Edit Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem'
        }}
        onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div style={{
            background: 'white',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '900px',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            overflow: 'hidden'
          }}>
            {/* Modal header */}
            <div style={{
              padding: '1.25rem 1.5rem',
              borderBottom: '2px solid #2563eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <h3 style={{ margin: 0, color: '#1e40af', fontSize: '1.2rem' }}>
                  {resubmitRevisionId ? '🔄 Revise & Resubmit' : '✏️ Suggest Edit'} — {sectionConfig.label}
                </h3>
                <p style={{ margin: '0.25rem 0 0', color: '#6b7280', fontSize: '0.85rem' }}>
                  {resubmitRevisionId
                    ? 'Make the requested changes and resubmit for review.'
                    : isOwner || user?.is_admin
                      ? 'Your edit will be applied immediately.'
                      : 'Your edit will be submitted for review by the amp owner or an admin.'}
                </p>
              </div>
              <button
                onClick={closeModal}
                style={{
                  background: 'none', border: 'none', fontSize: '1.5rem',
                  cursor: 'pointer', color: '#9ca3af', padding: '0.25rem'
                }}
              >
                ✕
              </button>
            </div>

            {/* Editor body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem' }}>
              {submitResult ? (
                <div style={{
                  padding: '1.5rem',
                  borderRadius: '10px',
                  background: submitResult.success ? '#f0fdf4' : '#fef2f2',
                  border: `1px solid ${submitResult.success ? '#bbf7d0' : '#fecaca'}`,
                  textAlign: 'center'
                }}>
                  <p style={{
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    color: submitResult.success ? '#065f46' : '#991b1b',
                    margin: 0
                  }}>
                    {submitResult.success ? '✅' : '❌'} {submitResult.message}
                  </p>
                </div>
              ) : (
                <>
                  {/* Reviewer notes banner for resubmission */}
                  {resubmitRevisionId && (resubmitReviewNote || resubmitHasSuggestedEdits) && (
                    <div style={{
                      padding: '0.75rem 1rem',
                      background: '#fffbeb',
                      border: '1px solid #fbbf24',
                      borderRadius: '8px',
                      marginBottom: '1rem',
                      display: 'flex',
                      gap: '0.5rem',
                      alignItems: 'flex-start'
                    }}>
                      <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>💬</span>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#92400e', marginBottom: '0.25rem' }}>
                          Reviewer Notes
                        </div>
                        {resubmitReviewNote && (
                          <div style={{ fontSize: '0.9rem', color: '#78350f', lineHeight: 1.5 }}>
                            {resubmitReviewNote}
                          </div>
                        )}
                        {resubmitHasSuggestedEdits && (
                          <div style={{ fontSize: '0.85rem', color: '#065f46', fontWeight: 600, marginTop: resubmitReviewNote ? '0.4rem' : 0 }}>
                            ✏️ The reviewer made edits to your text — see the tracked changes below.
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Tracked changes showing reviewer's edits (when reviewer made suggested edits) */}
                  {resubmitRevisionId && resubmitHasSuggestedEdits && (
                    <TrackedChangesView
                      oldContent={resubmitOriginalContent}
                      newContent={resubmitSuggestedContent}
                      label="Reviewer's Changes"
                      style={{ marginBottom: '1rem' }}
                    />
                  )}

                  <div style={{ marginBottom: '1rem' }}>
                    <RichTextEditor
                      value={editorContent}
                      onChange={setEditorContent}
                      placeholder={sectionConfig.placeholder}
                      amplifierId={amplifierId}
                      token={token}
                    />
                  </div>

                  {/* Edit summary */}
                  <div style={{ marginBottom: '0.5rem' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: summaryNeeded ? '#ef4444' : '#374151', marginBottom: '0.4rem', fontSize: '0.9rem' }}>
                      Edit Summary <span style={{ color: '#ef4444' }}>*</span>
                      {summaryNeeded && (
                        <span style={{ fontWeight: '500', marginLeft: '0.5rem', fontSize: '0.85rem' }}>
                          — Please describe what you changed
                        </span>
                      )}
                    </label>
                    <input
                      ref={summaryInputRef}
                      type="text"
                      value={editSummary}
                      onChange={(e) => { setEditSummary(e.target.value); if (e.target.value.trim()) setSummaryNeeded(false); }}
                      placeholder="Briefly describe what you changed and why (e.g., 'Added circuit revision details for 1972 model')"
                      maxLength={500}
                      style={{
                        width: '100%',
                        padding: '0.6rem 0.75rem',
                        border: summaryNeeded ? '2px solid #ef4444' : '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '0.9rem',
                        boxSizing: 'border-box',
                        transition: 'border-color 0.2s ease'
                      }}
                    />
                    <div style={{ textAlign: 'right', fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem' }}>
                      {editSummary.length}/500
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Modal footer */}
            {!submitResult && (
              <div style={{
                padding: '1rem 1.5rem',
                borderTop: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '0.75rem'
              }}>
                <button
                  onClick={closeModal}
                  style={{
                    background: '#f3f4f6',
                    color: '#374151',
                    border: '1px solid #d1d5db',
                    borderRadius: '10px',
                    padding: '0.6rem 1.25rem',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '0.9rem'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting || (sectionType === 'description' && !editorContent.trim())}
                  style={{
                    background: submitting || (sectionType === 'description' && !editorContent.trim()) ? '#93c5fd' : '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '0.6rem 1.5rem',
                    cursor: submitting || (sectionType === 'description' && !editorContent.trim()) ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem'
                  }}
                >
                  {submitting
                    ? 'Submitting...'
                    : resubmitRevisionId
                      ? 'Resubmit for Review'
                      : (isOwner || user?.is_admin ? 'Save Edit' : 'Submit for Review')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default WikiSuggestEdit;
